export type FrontendErrorCategory =
    | 'api_5xx'
    | 'api_4xx'
    | 'network'
    | 'chunk'
    | 'hydration'
    | 'runtime'

type ErrorKind = 'api' | 'network' | 'chunk' | 'hydration' | 'runtime'

export interface FrontendErrorInput {
    routeTemplate?: string
    chunkFile?: string
    category?: FrontendErrorCategory
    type?: ErrorKind
    message?: string
    url?: string
    route?: string
    status?: number
    releaseSha?: string
    userAgent?: string
    extra?: Record<string, unknown>
}

export interface FrontendErrorEvent {
    routeTemplate?: string
    chunkFile?: string
    category: FrontendErrorCategory
    message?: string
    url?: string
    route?: string
    status?: number
    releaseSha: string
    userAgent?: string
    extra?: Record<string, unknown>
}

export interface FrontendErrorLogEvent {
    routeTemplate?: string
    chunkFile?: string
    category: FrontendErrorCategory
    releaseSha: string
    status?: number
}

const FRONTEND_ERROR_CATEGORIES = new Set<FrontendErrorCategory>([
    'api_5xx',
    'api_4xx',
    'network',
    'chunk',
    'hydration',
    'runtime',
])
const ERROR_KINDS = new Set<ErrorKind>([
    'api',
    'network',
    'chunk',
    'hydration',
    'runtime',
])
const SENSITIVE_KEY_TERM_PATTERN =
    /\b(?:customer[_\s-]*id|access[_\s-]*token|token|secret|password|api[_\s-]*key|authorization|cookie|session|jwt|credentials?)\b/i
const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi
const PHONE_PATTERN = /(^|[^\w])(?:\+?\d[\d\s().-]{8,}\d)(?=$|[^\w])/g
const ALLOWED_FRONTEND_URL_PATTERN = /^(?:https?:\/\/|\/(?![\\/]))/i
const SENSITIVE_EXTRA_KEY_TERMS = [
    'token',
    'secret',
    'cookie',
    'authorization',
    'password',
    'payload',
    'body',
    'email',
    'phone',
    'customerid',
    'apikey',
    'credential',
    'session',
    'jwt',
]
const UNSAFE_EXTRA_KEYS = new Set(['proto', 'constructor', 'prototype'])
const MAX_DIAGNOSTIC_TEXT_LENGTH = 1_000
const MAX_EXTRA_DEPTH = 3
const MAX_EXTRA_ENTRIES = 20
export const MAX_FRONTEND_ERROR_EVENT_BYTES = 8_192

// Explicit page templates shared by all three sites. Never accept a raw pathname.
const FRONTEND_ROUTE_TEMPLATES = new Set([
    '/',
    '/about',
    '/cart',
    '/catalog',
    '/catalog/:product',
    '/contacts',
    '/create-order',
    '/delivery-and-payment',
    '/faq',
    '/media',
    '/news',
    '/news/:slug',
    '/portfolio',
    '/portfolio/:slug',
    '/pro',
    '/pro/:slug',
    '/rent',
    '/reviews',
    '/solutions',
    '/solutions/:slug',
    '/videos',
    '/warranty-and-service',
    '/:direction',
    '/:direction/:subcategory',
    '/sale',
    '/pay',
    '/delivery-russia',
    '/delivery-msk-and-spb',
    '/:product',
])
const CHUNK_FILE_PATTERN = /^[A-Za-z0-9_-]{1,128}\.js$/

export function sanitizeFrontendRouteTemplate(
    value: unknown
): string | undefined {
    return typeof value === 'string' && FRONTEND_ROUTE_TEMPLATES.has(value)
        ? value
        : undefined
}

export function frontendMatchedRouteTemplate(
    value: unknown
): string | undefined {
    if (typeof value !== 'string') return undefined
    // Nuxt adds empty parameter regexes to generated route records.
    return sanitizeFrontendRouteTemplate(value.replace(/\(\)/g, ''))
}

function sanitizeFrontendChunkFile(value: unknown): string | undefined {
    return typeof value === 'string' &&
        !hasUnsafeFrontendUrlCharacter(value) &&
        CHUNK_FILE_PATTERN.test(value)
        ? value
        : undefined
}

export function frontendChunkFile(
    error: unknown,
    origin: string
): string | undefined {
    if (!(error instanceof Error)) return undefined
    const match =
        /^(?:Failed to fetch dynamically imported module|error loading dynamically imported module): (.+)$/.exec(
            error.message
        )
    if (!match || hasUnsafeFrontendUrlCharacter(error.message)) return undefined
    const prefix = `${origin}/_nuxt/`
    // Check the literal URL before URL parsing could normalize traversal or escapes.
    return match[1].startsWith(prefix)
        ? sanitizeFrontendChunkFile(match[1].slice(prefix.length))
        : undefined
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function utf8ByteLength(value: string): number {
    return new TextEncoder().encode(value).byteLength
}

function utf8CharacterLength(character: string): number {
    const codePoint = character.codePointAt(0) ?? 0

    if (codePoint <= 0x7f) return 1
    if (codePoint <= 0x7ff) return 2
    if (codePoint <= 0xffff) return 3

    return 4
}

function hasUnsafeFrontendUrlCharacter(value: string): boolean {
    for (const character of value) {
        const codePoint = character.codePointAt(0) ?? 0

        if (character === '\\' || codePoint <= 0x1f || codePoint === 0x7f) {
            return true
        }
    }

    return false
}

function isSensitiveFrontendExtraKey(key: string): boolean {
    const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, '')

    return (
        UNSAFE_EXTRA_KEYS.has(normalizedKey) ||
        SENSITIVE_EXTRA_KEY_TERMS.some((term) => normalizedKey.includes(term))
    )
}

function truncateFrontendText(value: string): string {
    let bytes = 0
    let end = 0

    for (const character of value) {
        const characterBytes = utf8CharacterLength(character)

        if (bytes + characterBytes > MAX_DIAGNOSTIC_TEXT_LENGTH) break

        bytes += characterBytes
        end += character.length
    }

    return value.slice(0, end)
}

function redactSensitiveScalar(value: string): string {
    const match = value.match(SENSITIVE_KEY_TERM_PATTERN)

    if (!match || match.index === undefined) return value

    return `${value.slice(0, match.index)}[redacted]`
}

function sanitizeFrontendText(value: string): string {
    const sanitized = redactSensitiveScalar(truncateFrontendText(value))
        .replace(EMAIL_PATTERN, '[redacted-email]')
        .replace(PHONE_PATTERN, '$1[redacted-phone]')

    return truncateFrontendText(sanitized)
}

function normalizeFrontendErrorInput(input: unknown): FrontendErrorInput {
    if (!isRecord(input)) return {}

    const normalized: FrontendErrorInput = {}
    if (Object.prototype.hasOwnProperty.call(input, 'routeTemplate')) {
        const routeTemplate = sanitizeFrontendRouteTemplate(input.routeTemplate)
        if (routeTemplate) normalized.routeTemplate = routeTemplate
    }
    if (Object.prototype.hasOwnProperty.call(input, 'chunkFile')) {
        const chunkFile = sanitizeFrontendChunkFile(input.chunkFile)
        if (chunkFile) normalized.chunkFile = chunkFile
    }
    if (
        typeof input.category === 'string' &&
        FRONTEND_ERROR_CATEGORIES.has(input.category as FrontendErrorCategory)
    ) {
        normalized.category = input.category as FrontendErrorCategory
    }
    if (
        typeof input.type === 'string' &&
        ERROR_KINDS.has(input.type as ErrorKind)
    ) {
        normalized.type = input.type as ErrorKind
    }
    for (const key of [
        'message',
        'url',
        'route',
        'releaseSha',
        'userAgent',
    ] as const) {
        if (typeof input[key] === 'string') normalized[key] = input[key]
    }
    if (
        typeof input.status === 'number' &&
        Number.isInteger(input.status) &&
        input.status >= 100 &&
        input.status <= 599
    ) {
        normalized.status = input.status
    }
    if (isRecord(input.extra)) normalized.extra = input.extra

    return normalized
}

export function parseFrontendErrorRequestBody(
    body: unknown
): FrontendErrorInput {
    let parsedBody = body

    if (typeof parsedBody === 'string') {
        if (utf8ByteLength(parsedBody) > MAX_FRONTEND_ERROR_EVENT_BYTES) {
            return {}
        }
        try {
            parsedBody = JSON.parse(parsedBody)
        } catch {
            return {}
        }
    }

    return normalizeFrontendErrorInput(parsedBody)
}

export function sanitizeFrontendUrl(url?: string): string | undefined {
    if (!url) return undefined
    if (url !== url.trim() || hasUnsafeFrontendUrlCharacter(url)) {
        return undefined
    }
    if (!ALLOWED_FRONTEND_URL_PATTERN.test(url)) return undefined

    const boundedUrl = truncateFrontendText(url)

    try {
        const parsedUrl = new URL(boundedUrl, 'https://www.cromi.ru')

        if (!['http:', 'https:'].includes(parsedUrl.protocol)) return undefined

        const pathname = parsedUrl.pathname

        return pathname ? truncateFrontendText(pathname) : undefined
    } catch {
        if (!boundedUrl.startsWith('/')) return undefined

        const pathname = boundedUrl.split(/[?#\r\n]/, 1)[0]

        return pathname ? truncateFrontendText(pathname) : undefined
    }
}

export function sanitizeFrontendMessage(message?: string): string | undefined {
    return message ? sanitizeFrontendText(message) : undefined
}

export function classifyFrontendError(
    input: Pick<FrontendErrorInput, 'status' | 'type' | 'message'>
): FrontendErrorCategory {
    if (input.type === 'chunk') return 'chunk'
    if (input.type === 'hydration') return 'hydration'
    if (input.type === 'network') return 'network'

    if (input.status) {
        if (input.status >= 500) return 'api_5xx'
        if (input.status >= 400) return 'api_4xx'
    }

    return 'runtime'
}

function sanitizeFrontendExtraValue(
    value: unknown,
    depth: number,
    seen: WeakSet<object>
): unknown | undefined {
    if (typeof value === 'string') return sanitizeFrontendText(value)
    if (
        value === null ||
        typeof value === 'boolean' ||
        (typeof value === 'number' && Number.isFinite(value))
    ) {
        return value
    }
    if (
        depth >= MAX_EXTRA_DEPTH ||
        typeof value !== 'object' ||
        seen.has(value)
    ) {
        return undefined
    }

    seen.add(value)

    if (Array.isArray(value)) {
        return value
            .slice(0, MAX_EXTRA_ENTRIES)
            .map((item) => sanitizeFrontendExtraValue(item, depth + 1, seen))
            .filter((item) => item !== undefined)
    }

    const entries = Object.entries(value)
        .filter(([key]) => !isSensitiveFrontendExtraKey(key))
        .slice(0, MAX_EXTRA_ENTRIES)
        .flatMap(([key, item]) => {
            const safeValue = sanitizeFrontendExtraValue(item, depth + 1, seen)

            return safeValue === undefined ? [] : [[key, safeValue]]
        })

    return Object.fromEntries(entries)
}

export function sanitizeFrontendExtra(
    extra?: Record<string, unknown>
): Record<string, unknown> | undefined {
    if (!extra) return undefined

    const safeExtra = sanitizeFrontendExtraValue(extra, 0, new WeakSet())

    return isRecord(safeExtra) ? safeExtra : undefined
}

function fitFrontendErrorEventToBudget(
    event: FrontendErrorEvent
): FrontendErrorEvent {
    let boundedEvent = { ...event }
    const optionalFieldRemovers = [
        ({ extra: _extra, ...rest }: FrontendErrorEvent) => rest,
        ({ userAgent: _userAgent, ...rest }: FrontendErrorEvent) => rest,
        ({ route: _route, ...rest }: FrontendErrorEvent) => rest,
        ({ url: _url, ...rest }: FrontendErrorEvent) => rest,
        ({ message: _message, ...rest }: FrontendErrorEvent) => rest,
        ({ status: _status, ...rest }: FrontendErrorEvent) => rest,
    ]

    for (const removeOptionalField of optionalFieldRemovers) {
        if (
            utf8ByteLength(JSON.stringify(boundedEvent)) <=
            MAX_FRONTEND_ERROR_EVENT_BYTES
        ) {
            return boundedEvent
        }

        boundedEvent = removeOptionalField(boundedEvent)
    }

    return { category: event.category, releaseSha: '[truncated]' }
}

export function buildFrontendErrorEvent(
    input: FrontendErrorInput
): FrontendErrorEvent {
    const normalizedInput = normalizeFrontendErrorInput(input)
    const event: FrontendErrorEvent = {
        category:
            normalizedInput.category ?? classifyFrontendError(normalizedInput),
        releaseSha: truncateFrontendText(
            normalizedInput.releaseSha || 'unknown'
        ),
    }

    const message = sanitizeFrontendMessage(normalizedInput.message)
    const url = sanitizeFrontendUrl(normalizedInput.url)
    const route = sanitizeFrontendUrl(normalizedInput.route)
    const extra = sanitizeFrontendExtra(normalizedInput.extra)

    if (normalizedInput.routeTemplate)
        event.routeTemplate = normalizedInput.routeTemplate
    if (normalizedInput.chunkFile) event.chunkFile = normalizedInput.chunkFile

    if (message) event.message = message
    if (url) event.url = url
    if (route) event.route = route
    if (normalizedInput.status) event.status = normalizedInput.status
    if (normalizedInput.userAgent) {
        event.userAgent = truncateFrontendText(normalizedInput.userAgent)
    }
    if (extra) event.extra = extra

    return fitFrontendErrorEventToBudget(event)
}

export function sanitizeFrontendErrorForLog(
    input: unknown,
    serverReleaseSha?: string
): FrontendErrorLogEvent {
    const normalizedInput = normalizeFrontendErrorInput(input)
    const event: FrontendErrorLogEvent = {
        category:
            normalizedInput.category ?? classifyFrontendError(normalizedInput),
        releaseSha: truncateFrontendText(serverReleaseSha || 'unknown'),
    }

    if (normalizedInput.status) event.status = normalizedInput.status

    if (normalizedInput.routeTemplate)
        event.routeTemplate = normalizedInput.routeTemplate
    if (normalizedInput.chunkFile) event.chunkFile = normalizedInput.chunkFile

    return event
}
